import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  Tooltip,
  Spinner,
  useTheme,
  useColorMode,
  Select,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Wrap,
  WrapItem,
  Menu,
  MenuButton,
  MenuList,
  Checkbox,
  CheckboxGroup,
  Stack,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

import {
  faArrowDown,
  faArrowUp,
  faChevronLeft,
  faChevronRight,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { cqAPI } from "../../lib/Auth";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./CallReport.css";

const CONVERSATION_TOPICS_LIST = [
  "Appointment Management",
  "Personal Data Handling",
  "Prescriptions",
  "Technology and Equipment",
  "Customer Service",
  "Billing and Payments",
  "Medical Records",
  "Promotions and Offers",
  "Insurance Handling",
  "Treatment and Services",
  "COVID-19 Specific",
  "Communication Preferences",
  "Appointments and Visits",
  "Dental Health",
  "Health and Safety",
  "Legal and Compliance",
  "Product Information",
];

function CallReport() {
  const [calls, setCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState("Today");
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const [phoneNumber, setPhoneNumber] = useState("");
  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [startDate, endDate] = dateRange;
  const [campaigns, setCampaigns] = useState([]);
  const [campaign, setCampaign] = useState("");

  const [selectedTopics, setSelectedTopics] = useState([]);

  const theme = useTheme();
  const { colorMode } = useColorMode();

  const serializeTopics = (topicsArr) =>
    (topicsArr || []).map((t) => encodeURIComponent(t)).join(",");

  const deserializeTopics = (csv) =>
    (csv || "")
      .split(",")
      .map((s) => decodeURIComponent(s.trim()))
      .filter(Boolean);

  const updateUrlHash = (
    startDate,
    endDate,
    campaign,
    phoneNumber,
    selectedRange,
    topicsArr
  ) => {
    const queryParams = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      campaign,
      phoneNumber,
      selectedRange,
      conversationTopics: serializeTopics(topicsArr),
    }).toString();
    window.location.hash = queryParams;
  };

  const sortedCalls = useMemo(() => {
    let sortableItems = [...calls];
    if (sortConfig !== null && sortConfig.key) {
      sortableItems.sort((a, b) => {
        const va =
          sortConfig.key === "created"
            ? new Date(a[sortConfig.key])
            : a[sortConfig.key];
        const vb =
          sortConfig.key === "created"
            ? new Date(b[sortConfig.key])
            : b[sortConfig.key];

        if (va < vb) return sortConfig.direction === "ascending" ? -1 : 1;
        if (va > vb) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [calls, sortConfig]);

  const fetchCalls = async (params = {}) => {
    setIsLoading(true);

    try {
      const formattedStartDate = params.startDate
        ? moment(params.startDate).format("L")
        : moment(dateRange[0]).format("L");
      const formattedEndDate = params.endDate
        ? moment(params.endDate).format("L")
        : moment(dateRange[1]).format("L");
      const campaignParam = params.campaign ?? campaign;
      const phoneNumberParam = params.phoneNumber ?? phoneNumber;

      const topicsParam =
        params.conversationTopics ??
        params.conversation_topics ??
        selectedTopics;

      const qs = new URLSearchParams({
        rangeBeg: formattedStartDate,
        rangeEnd: formattedEndDate,
        phone_number: phoneNumberParam,
        campaign: campaignParam,
      });

      if (topicsParam && topicsParam.length) {
        qs.set("conversation_topics", serializeTopics(topicsParam));
      }

      const response = await cqAPI.get(`/reports/calls?${qs.toString()}`);

      setCalls(response.data.phonecalls || []);
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error("Error fetching calls:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleCampaignSelection = (value) => {
    setCampaign(value);
  };

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    let start, end;
    switch (value) {
      case "Today":
        start = moment().startOf("day");
        end = moment().endOf("day");
        break;
      case "Yesterday":
        start = moment().subtract(1, "days").startOf("day");
        end = moment().subtract(1, "days").endOf("day");
        break;
      case "Last 7 days":
        start = moment().subtract(6, "days").startOf("day");
        end = moment().endOf("day");
        break;
      case "Last 4 weeks":
        start = moment().subtract(4, "weeks").startOf("day");
        end = moment().endOf("day");
        break;
      case "Month to Date":
        start = moment().startOf("month");
        end = moment().endOf("day");
        break;
      case "Quarter to date":
        start = moment().startOf("quarter");
        end = moment().endOf("day");
        break;
      case "Year to date":
        start = moment().startOf("year");
        end = moment().endOf("day");
        break;
      case "Custom":
      default:
        start = null;
        end = null;
    }
    setDateRange([start ? start.toDate() : null, end ? end.toDate() : null]);
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <Flex direction="column" width="100%" paddingLeft="20px" paddingRight="20px">
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500" mr={2}>
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" paddingTop="10px">
        <Box as="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>
          {date.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </Box>
        <Box as="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const startDate = hashParams.get("startDate")
      ? moment(hashParams.get("startDate")).toDate()
      : moment().startOf("day").toDate();
    const endDate = hashParams.get("endDate")
      ? moment(hashParams.get("endDate")).toDate()
      : moment().endOf("day").toDate();
    const campaign = hashParams.get("campaign") || "";
    const phoneNumber = hashParams.get("phoneNumber") || "";
    const selectedRange = hashParams.get("selectedRange") || "Today";
    const topicsCsv = hashParams.get("conversationTopics") || "";
    const topics = deserializeTopics(topicsCsv);

    setDateRange([startDate, endDate]);
    setCampaign(campaign);
    setPhoneNumber(phoneNumber);
    setSelectedRange(selectedRange);
    setSelectedTopics(topics);

    fetchCalls({
      startDate,
      endDate,
      campaign,
      phoneNumber,
      selectedRange,
      conversationTopics: topics,
    });
  }, []);

  useEffect(() => {
    updateUrlHash(
      startDate,
      endDate,
      campaign,
      phoneNumber,
      selectedRange,
      selectedTopics
    );
  }, [startDate, endDate, campaign, phoneNumber, selectedRange, selectedTopics]);

  const navigateToCall = (callId) => {
    const newSearchParams = new URLSearchParams(window.location.search);
    navigate(`/reports/calls/${callId}?${newSearchParams.toString()}`);
  };

  if (isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
        position="relative"
      >
        <Spinner className="centered-spinner" color="blue.500" size="xl" />
      </Flex>
    );
  }

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    const regex = /^[0-9]*$/;
    if (value === "" || regex.test(value)) {
      setPhoneNumber(value);
    }
  };

  const getCallStatus = (call) => {
    if (call.auto_answered) return "Answered by Autopilot";
    if (call.vm) return "Voicemail";
    if (!call.pickedup && call.type === "Incoming") return "Call Ended before Response";
    if ((!call.duration || call.duration === "0") && call.type === "Outgoing")
      return "Unanswered Call";
    return "";
  };

  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justifyContent="flex-start" mb={4} wrap="wrap" gap={2}>
        <Flex align="center" mr={2} mb={2}>
          <Select
            placeholder="Select range"
            value={selectedRange}
            onChange={(e) => handleDateRangeSelection(e.target.value)}
            className="date-type"
          >
            <option value="Today">Today</option>
            <option value="Yesterday">Yesterday</option>
            <option value="Last 7 days">Last 7 days</option>
            <option value="Last 4 weeks">Last 4 weeks</option>
            <option value="Month to Date">Month to Date</option>
            <option value="Quarter to date">Quarter to date</option>
            <option value="Year to date">Year to date</option>
            <option value="Custom">Custom</option>
          </Select>
        </Flex>

        <Flex align="center" mr={2} mb={2}>
          <DatePicker
            className={`custom-datepicker ${
              colorMode === "light" ? "light-theme" : "dark-theme"
            }`}
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => {
              setDateRange(update);
              setSelectedRange("Custom");
            }}
            isClearable
            renderCustomHeader={renderCustomHeader}
          />
        </Flex>

        <Flex align="center" mr={2} mb={2}>
          <Select value={campaign} onChange={(e) => handleCampaignSelection(e.target.value)}>
            <option value="">Select Origin</option>
            {campaigns.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name.substr(0, 30)}
              </option>
            ))}
          </Select>
        </Flex>

        <Flex align="center" mr={2} mb={2}>
          <Input
            placeholder="Phone number"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            width="150px"
            type="tel"
            pattern="^\\+?[0-9]{1,3}?[-. ]?([0-9]{2,3}[-. ]?){2,3}[0-9]{3,4}$"
            title="Phone number must be in a valid format (international or local)"
          />
        </Flex>

        {/* Chakra dropdown for multi-select topics with fixed width button */}
        <Flex align="center" mr={2} mb={2}>
          <Menu closeOnSelect={false} placement="bottom-start">
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              variant="outline"
              w="260px"            // fixed width so layout doesn't shift
              justifyContent="space-between"
            >
              <Flex align="center" w="100%" gap={2}>
                <Box flex="1" isTruncated>
                  Topics
                </Box>
                <Tag size="sm" variant="subtle">
                  {selectedTopics.length}
                </Tag>
              </Flex>
            </MenuButton>
            <MenuList p={2} maxH="280px" overflowY="auto" minW="280px">
              <CheckboxGroup
                value={selectedTopics}
                onChange={(vals) => setSelectedTopics(vals)}
              >
                <Stack spacing={2}>
                  {CONVERSATION_TOPICS_LIST.map((topic) => (
                    <Checkbox key={topic} value={topic}>
                      {topic}
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
              <Flex mt={3} gap={2} justify="flex-end">
                <Button size="sm" variant="ghost" onClick={() => setSelectedTopics([])}>
                  Clear
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() =>
                    fetchCalls({
                      startDate,
                      endDate,
                      campaign,
                      phoneNumber,
                      conversationTopics: selectedTopics,
                    })
                  }
                >
                  Apply
                </Button>
              </Flex>
            </MenuList>
          </Menu>
        </Flex>

        <Flex ml="auto" mb={2}>
          <Button
            leftIcon={<FontAwesomeIcon icon={faPhone} />}
            onClick={() =>
              fetchCalls({
                startDate,
                endDate,
                campaign,
                phoneNumber,
                conversationTopics: selectedTopics,
              })
            }
          >
            Show calls
          </Button>
        </Flex>
      </Flex>

      <Table
        variant="simple"
        size="sm"
        tableLayout="fixed"
        width="100%"
        sx={{
          th: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", py: 2 },
          td: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", py: 2 },
        }}
      >
        <Thead>
          <Tr>
            <Th onClick={() => requestSort("type")} cursor="pointer" width="40px" textAlign="center">
              <Tooltip label="Direction" hasArrow>
                <Box as="span">
                  <FontAwesomeIcon icon={faPhone} />
                </Box>
              </Tooltip>
            </Th>
            <Th onClick={() => requestSort("created")} cursor="pointer" width="120px">
              Date
            </Th>
            <Th onClick={() => requestSort("patient_name")} cursor="pointer" width="140px">
              Patient
            </Th>
            <Th onClick={() => requestSort("campaign")} cursor="pointer" width="120px">
              Origin
            </Th>
            <Th onClick={() => requestSort("from")} cursor="pointer" width="140px">
              Phone
            </Th>
            <Th onClick={() => requestSort("duration")} cursor="pointer" width="80px">
              Dur.
            </Th>
            <Th width="220px">Topics</Th>
            <Th width="160px">Status</Th>
          </Tr>
        </Thead>
        <Tbody fontSize="sm">
          {sortedCalls.map((call) => (
            <Tr
              key={call._id}
              _hover={{ bg: hoverBgColor, cursor: "pointer" }}
              onClick={() => navigateToCall(call._id)}
            >
              <Td textAlign="center">
                <Tooltip
                  label={call.type === "Incoming" ? "Incoming" : "Outgoing"}
                  hasArrow
                >
                  <Box>
                    <FontAwesomeIcon
                      icon={call.type === "Incoming" ? faArrowDown : faArrowUp}
                    />
                  </Box>
                </Tooltip>
              </Td>
              <Td>{call.created}</Td>
              <Td>{call.patient_name || call.name}</Td>
              <Td>
                {call.type === "Incoming"
                  ? call.campaign || "N/A"
                  : call.from_ext
                  ? `Ext: ${call.from_ext}`
                  : call?.is_campaign
                  ? "Auto"
                  : "N/A"}
              </Td>
              <Td>{call.type === "Incoming" ? call.from : call.to}</Td>
              <Td>{call.duration}</Td>
              <Td>
                <Wrap spacing="1" shouldWrapChildren>
                  {(call.conversation_topics || []).map((topic, i) => (
                    <WrapItem key={`${call._id}-topic-${i}`}>
                      <Tag size="sm" variant="subtle">
                        {topic}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </Td>
              <Td>{getCallStatus(call)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

export default CallReport;
